import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConsentCheckbox } from '@/components/ConsentCheckbox';
import { PublicFooter } from '@/components/PublicFooter';
import axios from 'axios';

function formatApiError(detail) {
  if (detail == null) return 'Algo correu mal. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [consentVersion, setConsentVersion] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const navigate = useNavigate();
  const { login, register, user } = useAuth();

  // Show message from Google auth redirect (pendente)
  useEffect(() => {
    const state = window.history.state?.usr;
    if (state?.message) { setSuccess(state.message); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/site-content`);
        setConsentVersion(`${data.terms_and_conditions_updated_at || ''}|${data.privacy_policy_updated_at || ''}`);
      } catch {}
    })();
  }, []);

  if (user) {
    const dest = (user.role === 'admin' || user.role === 'nutricionista') ? '/admin/inicio' : (user.role === 'viewer' ? '/viewer/dashboard' : '/dashboard');
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (isRegister) {
        const result = await register(email, password, name, consentVersion);
        if (result?.status === 'pendente') {
          setSuccess(result.message || 'Registo recebido. A tua conta será validada pela administradora.');
          setIsRegister(false);
          setEmail(''); setPassword(''); setName('');
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        const userData = await login(email, password);
        const dest = (userData.role === 'admin' || userData.role === 'nutricionista') ? '/admin/inicio' : (userData.role === 'viewer' ? '/viewer/dashboard' : '/dashboard');
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotMsg(data.message || 'Se o email existir, receberás um link de recuperação.');
    } catch (err) {
      setForgotMsg('Erro ao processar o pedido. Tenta novamente.');
    } finally { setForgotLoading(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="font-heading font-semibold text-4xl text-brand-green tracking-tight" data-testid="login-logo">nutrianapereira</h1>
          <p className="mt-2 text-brand-muted text-sm">Alimentação saudável para toda a família</p>
        </div>

        <div className="bg-white border border-brand-border rounded-3xl p-6 shadow-[0_8px_32px_rgba(101,113,97,0.04)]">
          <h2 className="font-heading font-medium text-xl text-brand-text mb-6" data-testid="login-title">
            {isRegister ? 'Pedir acesso' : 'Entrar'}
          </h2>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-600" data-testid="login-error">{error}</div>}
          {success && <div className="mb-4 p-3 bg-brand-mint/20 border border-brand-mint rounded-2xl text-sm text-brand-green" data-testid="login-success">{success}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-sm text-brand-muted mb-1.5 block">Nome</Label>
                <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="O teu nome" className="rounded-2xl border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green" required data-testid="register-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-sm text-brand-muted mb-1.5 block">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.pt" className="rounded-2xl border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green" required data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm text-brand-muted mb-1.5 block">Palavra-passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="A tua palavra-passe" className="rounded-2xl border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green" required minLength={6} data-testid="login-password-input" />
              {!isRegister && (
                <button type="button" onClick={() => { setShowForgot(true); setForgotMsg(''); setForgotEmail(''); }} className="text-xs text-brand-green hover:underline mt-1.5 block" data-testid="forgot-password-link">
                  Esqueceste a palavra-passe?
                </button>
              )}
            </div>
            {isRegister && (
              <ConsentCheckbox checked={consent} onChange={setConsent} testId="consent-checkbox" />
            )}
            <button type="submit" disabled={loading || (isRegister && !consent)} className="w-full bg-brand-green text-white hover:bg-brand-green-hover transition-all duration-300 rounded-full px-6 py-3.5 font-medium disabled:opacity-50" data-testid="login-submit-button">
              {loading ? 'A processar...' : (isRegister ? 'Pedir acesso' : 'Entrar')}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-brand-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-brand-muted">ou</span></div>
          </div>

          <button onClick={handleGoogleLogin} className="w-full border border-brand-border text-brand-text hover:bg-brand-bg transition-all duration-300 rounded-full px-6 py-3.5 font-medium flex items-center justify-center gap-3" data-testid="google-login-button">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuar com Google
          </button>

          <p className="text-center text-sm text-brand-muted mt-6">
            {isRegister ? 'Já tens conta?' : 'Precisas de acesso?'}{' '}
            <button onClick={() => { setIsRegister(!isRegister); setError(''); setSuccess(''); }} className="text-brand-green font-medium hover:underline" data-testid="toggle-auth-mode">
              {isRegister ? 'Entrar' : 'Pedir acesso'}
            </button>
          </p>
        </div>
      </div>
      <div className="w-full max-w-sm"><PublicFooter /></div>
      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="rounded-3xl border-brand-border max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Recuperar palavra-passe</DialogTitle></DialogHeader>
          {forgotMsg ? (
            <div className="space-y-4">
              <div className="p-3 bg-brand-mint/20 border border-brand-mint rounded-2xl text-sm text-brand-green" data-testid="forgot-success">{forgotMsg}</div>
              <button onClick={() => setShowForgot(false)} className="w-full border border-brand-border text-brand-text rounded-full py-2.5 text-sm font-medium hover:bg-brand-bg" data-testid="forgot-close-btn">Fechar</button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-brand-muted">Introduz o teu email e enviaremos um link para redefinires a tua palavra-passe.</p>
              <div>
                <Label htmlFor="forgot-email" className="text-sm text-brand-muted mb-1.5 block">Email</Label>
                <Input id="forgot-email" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="email@exemplo.pt" className="rounded-2xl border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green" required data-testid="forgot-email-input" />
              </div>
              <button type="submit" disabled={forgotLoading} className="w-full bg-brand-green text-white hover:bg-brand-green-hover transition-all rounded-full px-6 py-3.5 font-medium disabled:opacity-50" data-testid="forgot-submit-btn">
                {forgotLoading ? 'A enviar...' : 'Enviar link de recuperação'}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
